import { AHText, Box, useSpaceSize } from "arkh";
import React, { useMemo, useRef, useState } from "react";
import { View, useWindowDimensions } from "react-native";
import { AllWidgets, WidgetLayoutProvider, useWidgetsToPos } from "widgets";
import { DashboardHeader } from "./StorecastHeader";
import { useUnitSize } from "../../OS/UnitSizeProvider";
import { useFragment, graphql, useSubscription } from "react-relay";

import type { StorecastChangedTVSubscription } from "./__generated__/StorecastChangedTVSubscription.graphql";
import type { StorecastLayoutChangedTVSubscription } from "./__generated__/StorecastLayoutChangedTVSubscription.graphql";
import { ErrorBoundary } from "react-error-boundary";
import * as Sentry from "@sentry/react-native";

import { AllWidgets_widget$key } from "widgets/src/__generated__/AllWidgets_widget.graphql";
import { notEmpty } from "widgets/src/utils";
import { Storecast_storecastScreen$key } from "./__generated__/Storecast_storecastScreen.graphql";

const StorecastComponentC = (props: {
  storecast: Storecast_storecastScreen$key;
  touchMode: boolean;
  onSelectDashboard: (dashboardId: string) => void;

  pollFetchKey: string;
  fetchKey?: string;
}) => {
  const { width, height } = useWindowDimensions();
  const screen = useFragment(
    graphql`
      fragment Storecast_storecastScreen on StorecastScreen {
        ...StorecastHeaderTV_storecast
        dashboard(dashboardId: $dashboardId) @required(action: THROW) {
          id
          name
          widgets {
            id
            __typename
            ...useWidgetToPos_widget
            ...AllWidgets_widget
          }
        }
      }
    `,
    props.storecast
  );
  const storecast = screen.dashboard ?? null;

  const pixelShift = useRef(Math.round(Math.random() * 9));

  const unitSize = Math.floor(useUnitSize());
  const paddingHorizontal = Math.round((width - unitSize * 8) / 4);
  const margin = useSpaceSize("small");
  const storecastId = storecast.id;

  const leftHeight = height - (unitSize * 4 + margin * 2);
  const headerHeight = Math.min(leftHeight, unitSize / 2);
  useSubscription<StorecastLayoutChangedTVSubscription>(
    useMemo(
      () => ({
        variables: { storecastId },
        subscription: graphql`
          subscription StorecastLayoutChangedTVSubscription($storecastId: ID!) {
            widgetsRearranged(storecastId: $storecastId) {
              id
              widgets {
                ...useWidgetToPos_widget
              }
            }
          }
        `,
      }),
      [storecastId]
    )
  );
  useSubscription<StorecastChangedTVSubscription>(
    useMemo(
      () => ({
        updater: (store, data) => {
          data?.storecastChanged.widgets
            .filter(
              (w) =>
                w &&
                (w.__typename === "WidgetTodoOverview" ||
                  w.__typename === "WidgetIssueOverview" ||
                  w.__typename === "WidgetSignalOverview")
            )
            .forEach((w) => {
              if (w?.id) {
                store.get(w.id)?.invalidateRecord();
              }
            });
        },
        variables: { storecastId },
        subscription: graphql`
          subscription StorecastChangedTVSubscription($storecastId: ID!) {
            storecastChanged(storecastId: $storecastId) {
              id
              name
              widgets {
                id
                __typename
                ...useWidgetToPos_widget
                ...AllWidgets_widget
              }
            }
          }
        `,
      }),
      [storecastId]
    )
  );

  const positions = useWidgetsToPos(storecast.widgets.filter(notEmpty));
  return (
    <View
      style={{
        transform: [{ translateY: -Math.abs(pixelShift.current) }],
        flex: 1,
        paddingHorizontal,
      }}
    >
      <View
        style={{
          flex: 1,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          style={{
            height: leftHeight,
            width: unitSize * 8 - margin * 2,
            alignContent: "center",
            justifyContent: "flex-end",
          }}
          spaceHorizontal="xxsmall"
        >
          <DashboardHeader
            storecast={screen}
            onSelectDashboard={props.onSelectDashboard}
            height={headerHeight}
          />
        </Box>
        <View
          style={{
            width: unitSize * 8,
            height: unitSize * 4,
            position: "relative",
          }}
        >
          {positions.map(({ w, h, positionX, y, id }) => {
            const widget = storecast.widgets.find((w) => w?.id === id);
            if (!widget) {
              return null;
            }
            return (
              <WidgetLayoutProvider
                key={id}
                value={{
                  width: unitSize * w - margin * 2,
                  height: unitSize * h - margin * 2,
                  unitSize,
                  h,
                }}
              >
                <Box
                  style={[
                    { transition: "transform .3s" } as any,
                    {
                      position: "absolute",
                      transform: [
                        { translateY: y * unitSize },
                        { translateX: positionX * unitSize },
                      ],
                      width: w * unitSize,
                      height: h * unitSize,
                      padding: margin,
                    },
                  ]}
                >
                  <Box
                    style={{
                      height: "100%",
                      width: "100%",
                      overflow:
                        widget.__typename === "WidgetSpace"
                          ? "visible"
                          : "hidden",
                    }}
                    cornerRadius="medium"
                  >
                    <WidgetWithErrorBoundrary
                      widget={widget}
                      storecastId={storecastId}
                      touchMode={props.touchMode}
                      fetchKey={props.fetchKey}
                      pollFetchKey={props.pollFetchKey}
                    />
                  </Box>
                </Box>
              </WidgetLayoutProvider>
            );
          })}
        </View>
        <Box
          spaceHorizontal="widget"
          style={{
            flex: 1,
            width: "100%",
          }}
        />
      </View>
    </View>
  );
};

function WidgetWithErrorBoundraryFallbackRenderer() {
  return (
    <Box
      spaceHorizontal="medium"
      style={{
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <AHText spaceBottom="medium" type="title-small">
        Error
      </AHText>
    </Box>
  );
}

function WidgetWithErrorBoundrary(props: {
  widget: AllWidgets_widget$key;
  storecastId: string;
  touchMode: boolean;
  fetchKey?: string;
  pollFetchKey: string;
}) {
  const [fk, setFk] = useState(0);
  const margin = useSpaceSize("small");

  return (
    <ErrorBoundary
      onError={(err) => {
        console.log("error in widget");
        console.log(err);
        Sentry.captureException(err);
        setFk((k) => (k > 5 ? k : k + 1));
      }}
      fallbackRender={WidgetWithErrorBoundraryFallbackRenderer}
    >
      <AllWidgets
        margin={margin}
        idiom={props.touchMode ? "TOUCH_READONLY" : "STATIC"}
        fetchKey={fk + ":" + (props.fetchKey ?? "")}
        widget={props.widget}
        storecastId={props.storecastId}
        mode={props.touchMode ? "FOCUS" : "IDLE"}
        pollFetchKey={props.pollFetchKey}
      />
    </ErrorBoundary>
  );
}
export const Storecast = React.memo(StorecastComponentC);
